// S8 — Go-live checklist (`/onboarding/checklist`, Module 02 §4 S8, §3.6).
//
// The screen answers one question honestly: can this tenant go live, and if not,
// exactly what is missing. It reads `GET /v1/onboarding/checklist` — a PURE read
// that never evaluates — plus the session (for the wizard steps the go-live gate
// also requires, and for the live/frozen state).
//
// The four states that matter:
//  1. NEVER EVALUATED (`items: []`, score 0): render the 19 catalog rows as
//     `pending` placeholders plus a "Run evaluation" CTA. Never an empty page,
//     never an implied all-clear (§4 S8).
//  2. EVALUATED: six category sections, each item with its verdict, its weight,
//     WHY it holds (the evaluator's rule + evidence), and — for a staged item —
//     the owning module it is blocked on. Blocking items are visually distinct
//     and summarised at the top, because they are the gate.
//  3. REFUSED GO-LIVE: the server's own 422 (BLOCKING_ITEMS_UNMET /
//     SCORE_BELOW_THRESHOLD / PRECONDITIONS_UNMET) rendered as actions, by the
//     shared `GoLiveButton`.
//  4. LIVE: read-only history — when it went live and who took it live, every
//     mutation control gone (the API answers 409 ALREADY_LIVE, and an expected
//     409 is never surfaced as a raw error).

import { enumLabel } from "../../../lib/enum-label";
import { useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { CardSkeleton, ErrorState } from "../../../components/states";
import { requestIdOf } from "../../../lib/request";
import { GoLiveButton } from "../components/go-live-button";
import { ScoreDial } from "../components/score-dial";
import { RefLabel } from "../../../components/ref-label";
import { formatTimestamp } from "../../../lib/datetime";
import {
  useChecklist,
  useEvaluateChecklist,
  useOnboardingSession,
} from "../hooks/onboarding-queries";
import { useOnboardingPermissions } from "../hooks/permissions";
import { checklistItemLabelKey, isAlreadyLive } from "../lib/wizard-problems";
import {
  GO_LIVE_SCORE_THRESHOLD,
  STEP_COUNT,
  incompleteSteps,
  isWizardFrozen,
} from "../lib/wizard-steps";
import {
  CHECKLIST_TOTAL_WEIGHT,
  checklistRows,
  fixRouteOf,
  groupByCategory,
  isMet,
  isNeverEvaluated,
  type ChecklistItem,
} from "./checklist/checklist-catalog";
import { ChecklistItemRow, itemErrorMessage } from "./checklist/checklist-item";
import "./checklist/checklist.css";

/** A marker no translation contains, so the sentence can be cut around it. */
const USER_SLOT = "\u2063USER\u2063";

/**
 * "Taken live by <name>", with the name from the names read (spec 16 §3.13).
 * The sentence is split around the placeholder so every language keeps its own
 * word order and the name stays a live label rather than a string.
 */
function LiveBy({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const [before = "", after = ""] = t("onboarding.checklist.liveBy", { user: USER_SLOT }).split(
    USER_SLOT,
  );
  return (
    <span data-testid="checklist-live-by">
      {before}
      <RefLabel kind="user" id={userId} />
      {after}
    </span>
  );
}

export function ChecklistScreen() {
  const { t, i18n } = useTranslation();
  const perms = useOnboardingPermissions();
  const session = useOnboardingSession();
  const checklist = useChecklist();
  const evaluate = useEvaluateChecklist();
  // A category the reader opened or folded by hand. Untouched, a section is
  // open while any of its items is unmet and folds once every item is met
  // (passed or waived): the reader's attention goes to what is left to do,
  // and a fully earned section is one line (rev-7, D-447 ruling 4's rule).
  const [toggled, setToggled] = useState<Readonly<Record<string, boolean>>>({});
  // A category seen with an unmet item during this visit stays open when its
  // last item is met. Waiving that item refetches the checklist, and a fold
  // then would unmount the row the reader is working in and drop their focus
  // to the page. Only a section met on arrival starts folded.
  const seenUnmet = useRef<Set<string>>(new Set());

  if (session.isPending || checklist.isPending) {
    return (
      <div className="chk-screen">
        <h2>{t("onboarding.checklist.title")}</h2>
        <CardSkeleton lines={8} label={t("onboarding.state.loading")} />
      </div>
    );
  }

  if (checklist.error !== null) {
    return (
      <div className="chk-screen">
        <h2>{t("onboarding.checklist.title")}</h2>
        <ErrorState
          title={t("onboarding.checklist.errors.loadTitle")}
          detail={t("onboarding.checklist.errors.loadDetail")}
          retryLabel={t("onboarding.state.retry")}
          onRetry={() => void checklist.refetch()}
          requestId={requestIdOf(checklist.error)}
          requestIdLabel={t("onboarding.state.requestId")}
        />
      </div>
    );
  }

  const data = checklist.data;
  const sessionData = session.data;
  // The shell already blocks on a failed session, so this is belt-and-braces:
  // without the session there is no live state and no go-live control, but the
  // checklist itself still reads (it is a pure GET, open to every office role).
  const frozen = sessionData !== undefined && isWizardFrozen(sessionData.session);
  const neverEvaluated = isNeverEvaluated(data.items);
  const rows = checklistRows(data.items);
  const sections = groupByCategory(rows);
  const blockingUnmetRows = rows.filter(
    (r) => r.blocking && data.blocking_unmet.includes(r.item_key),
  );
  const canEvaluate = perms.has("onboarding.manage_wizard") && !frozen;
  // A 409 on evaluate after go-live is EXPECTED (the wizard is frozen); it is
  // reported as the live state, never as an error card.
  const evaluateError: unknown = isAlreadyLive(evaluate.error) ? null : evaluate.error;

  return (
    <div className="chk-screen">
      <header className="chk-head">
        <div>
          <h2>{t("onboarding.checklist.title")}</h2>
          <p className="chk-note">{t("onboarding.checklist.blurb")}</p>
          {frozen ? (
            <p className="chk-live" data-testid="checklist-live">
              {/* The shell's meta row already shows the Live badge; this line
                  says when and with what score, and who took it live. */}
              {sessionData.session.went_live_at !== null
                ? t("onboarding.checklist.liveOn", {
                    date: formatTimestamp(sessionData.session.went_live_at, i18n.language),
                    score: sessionData.session.completeness_score,
                  })
                : t("onboarding.checklist.liveHistory", {
                    score: sessionData.session.completeness_score,
                  })}
              {sessionData.session.went_live_by_user_id !== null ? (
                <>
                  {" "}
                  <LiveBy userId={sessionData.session.went_live_by_user_id} />
                </>
              ) : null}
            </p>
          ) : null}
        </div>
        <ScoreDial
          score={data.score}
          threshold={GO_LIVE_SCORE_THRESHOLD}
          blockingUnmet={data.blocking_unmet.length}
          neverEvaluated={neverEvaluated}
        />
      </header>

      <div className="chk-toolbar">
        <p className="chk-note">
          {t("onboarding.checklist.weightTotal", { total: CHECKLIST_TOTAL_WEIGHT })}
        </p>
        {canEvaluate ? (
          <Button
            busy={evaluate.isPending}
            onClick={() => {
              evaluate.mutate();
            }}
          >
            {evaluate.isPending
              ? t("onboarding.checklist.actions.evaluating")
              : t("onboarding.checklist.actions.evaluate")}
          </Button>
        ) : null}
      </div>

      {evaluateError !== null && evaluateError !== undefined ? (
        <p className="chk-error" role="alert" data-testid="checklist-evaluate-error">
          {itemErrorMessage(evaluateError, t)}
        </p>
      ) : null}

      {neverEvaluated ? (
        <div className="state state-empty" data-testid="checklist-never-evaluated">
          <p className="state-title">{t("onboarding.checklist.emptyTitle")}</p>
          <p className="state-body">{t("onboarding.checklist.emptyBody")}</p>
          {canEvaluate ? (
            <Button
              variant="primary"
              busy={evaluate.isPending}
              onClick={() => {
                evaluate.mutate();
              }}
            >
              {t("onboarding.checklist.actions.evaluate")}
            </Button>
          ) : (
            <p className="chk-note">{t("onboarding.checklist.emptyNoPermission")}</p>
          )}
        </div>
      ) : null}

      {blockingUnmetRows.length > 0 ? (
        <div className="chk-blocking-summary" role="status" data-testid="checklist-blocking">
          <p className="state-title">{t("onboarding.checklist.blockingTitle")}</p>
          <p className="chk-note">{t("onboarding.checklist.blockingBody")}</p>
          <ul className="chk-list">
            {blockingUnmetRows.map((item) => {
              const fixRoute = fixRouteOf(item);
              return (
                <li key={item.item_key}>
                  {t(checklistItemLabelKey(item.item_key), { defaultValue: item.item_key })}
                  {/* The gate says WHERE, not only what (§4 S8). */}
                  {fixRoute !== null ? (
                    <>
                      {" "}
                      <Link to={fixRoute} className="wizard-header-link">
                        {t("onboarding.checklist.fixThis")}
                      </Link>
                    </>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {sections.map((section) => {
        const allMet = section.items.every(isMet);
        if (!allMet) seenUnmet.current.add(section.category);
        const open = toggled[section.category] ?? seenUnmet.current.has(section.category);
        const listId = `chk-items-${section.category}`;
        return (
          <section
            className="chk-section"
            key={section.category}
            aria-labelledby={`chk-${section.category}`}
            data-testid="checklist-section"
            data-category={section.category}
            data-open={open ? "true" : "false"}
          >
            <div className="chk-section-head">
              <h3 id={`chk-${section.category}`}>
                <button
                  type="button"
                  className="chk-section-toggle"
                  aria-expanded={open}
                  aria-controls={open ? listId : undefined}
                  data-testid="checklist-section-toggle"
                  onClick={() => {
                    setToggled({ ...toggled, [section.category]: !open });
                  }}
                >
                  {enumLabel(t, "onboarding.checklist.categories", section.category)}
                  <span className="chk-section-count num">
                    {t("onboarding.checklist.sectionItems", { n: section.items.length })}
                  </span>
                </button>
              </h3>
              <p className="chk-note">
                {t("onboarding.checklist.sectionWeight", {
                  earned: section.earned,
                  total: section.total,
                })}
              </p>
            </div>
            {open ? (
              <ul className="chk-items" id={listId}>
                {section.items.map((item: ChecklistItem) => (
                  <ChecklistItemRow
                    key={item.item_key}
                    item={item}
                    canWaive={perms.canWaive(item.blocking)}
                    canManualSet={
                      perms.has("onboarding.checklist.manual_set") &&
                      (!item.blocking || perms.isGroupAdmin)
                    }
                    frozen={frozen}
                  />
                ))}
              </ul>
            ) : null}
          </section>
        );
      })}

      {sessionData !== undefined && !frozen ? (
        <section className="chk-golive" aria-labelledby="chk-golive-title">
          <h3 id="chk-golive-title">{t("onboarding.checklist.goLiveHeading")}</h3>
          <PreflightSummary
            score={data.score}
            blockingUnmet={data.blocking_unmet.length}
            eligible={data.go_live_eligible}
            incomplete={incompleteSteps(sessionData.steps)}
          />
          <GoLiveButton
            session={sessionData.session}
            steps={sessionData.steps}
            summary={sessionData.checklist_summary}
          />
        </section>
      ) : null}
    </div>
  );
}

/**
 * The pre-flight read of the §3.6 gate, from the checklist response itself:
 * steps completed, blocking items clear, score ≥ 85, and the server's own
 * `go_live_eligible`. It explains the button; the server re-decides all of it
 * inside the wizard advisory lock and is the only verdict that counts.
 */
function PreflightSummary({
  score,
  blockingUnmet,
  eligible,
  incomplete,
}: {
  score: number;
  blockingUnmet: number;
  eligible: boolean;
  incomplete: readonly number[];
}) {
  const { t } = useTranslation();
  const gap = Math.max(0, GO_LIVE_SCORE_THRESHOLD - score);
  return (
    <ul className="chk-preflight" data-testid="checklist-preflight">
      <PreflightRow
        ok={incomplete.length === 0}
        label={
          incomplete.length === 0
            ? t("onboarding.checklist.preflight.stepsDone", { total: STEP_COUNT })
            : t("onboarding.checklist.preflight.stepsPending", { steps: incomplete.join(", ") })
        }
      />
      <PreflightRow
        ok={blockingUnmet === 0}
        label={
          blockingUnmet === 0
            ? t("onboarding.checklist.preflight.blockingClear")
            : t("onboarding.checklist.preflight.blockingUnmet", { count: blockingUnmet })
        }
      />
      <PreflightRow
        ok={gap === 0}
        label={
          gap === 0
            ? t("onboarding.checklist.preflight.scoreOk", {
                score,
                required: GO_LIVE_SCORE_THRESHOLD,
              })
            : t("onboarding.checklist.preflight.scoreGap", {
                score,
                required: GO_LIVE_SCORE_THRESHOLD,
                gap,
              })
        }
      />
      <PreflightRow
        ok={eligible}
        label={
          eligible
            ? t("onboarding.checklist.preflight.serverEligible")
            : t("onboarding.checklist.preflight.serverNotEligible")
        }
      />
    </ul>
  );
}

function PreflightRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className={ok ? "chk-preflight-ok" : "chk-preflight-bad"}>
      <span aria-hidden="true">{ok ? "✓" : "•"}</span> {label}
    </li>
  );
}
